import {
  ContextType,
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';
import { Medialink } from '../../../../../api/medialink';
import { Module } from '../../../../../api/module';

export type ExerciseManagerContextValue = {
  showScore: boolean;
  medialink?: Medialink;
  phase: 'launch' | 'activity' | 'progression' | undefined;
  setPhase: Dispatch<SetStateAction<'launch' | 'activity' | 'progression' | undefined>>;
  module?: Module;
  studioGroup?: string;
  onClose: () => void;
  assignmentId?: string;
  language?: string;
};

export const ExerciseManagerContext = createContext<ExerciseManagerContextValue>({
  showScore: false,
  phase: undefined,
  setPhase: () => {},
  onClose: () => {},
});

interface ExerciseManagerContextProviderProps extends PropsWithChildren {
  medialink: Medialink;
  module: Module;
  onClose: () => void;
  studioGroup: string;
  assignmentId?: string;
  language: string;
}

export const ExerciseManagerContextProvider = ({
  children,
  medialink,
  module,
  onClose: handleClose,
  studioGroup,
  assignmentId,
  language,
}: ExerciseManagerContextProviderProps) => {
  const [phase, setPhase] = useState<'launch' | 'activity' | 'progression' | undefined>(undefined);

  const onClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  return (
    <ExerciseManagerContext.Provider
      value={{
        phase,
        setPhase,
        medialink,
        showScore: Boolean(
          medialink.eduhintExercise?.showScore ||
            medialink.edumaticExercise?.showScore ||
            medialink.assessmentQExercise?.showScore,
        ),
        module,
        onClose,
        studioGroup,
        assignmentId,
        language,
      }}
    >
      {medialink && module && studioGroup && language && children}
    </ExerciseManagerContext.Provider>
  );
};

function isValidExerciseManagerContext(
  context: ContextType<typeof ExerciseManagerContext>,
): asserts context is ExerciseManagerContextValue & {
  medialink: Medialink;
  module: Module;
  onClose: () => void;
  studioGroup: string;
  language: string;
} {
  if (!context.medialink) {
    throw new Error('ExerciseManagerContext must be provided with a medialink.');
  }

  if (!context.module) {
    throw new Error('ExerciseManagerContext must be provided with a module.');
  }

  if (!context.onClose) {
    throw new Error('ExerciseManagerContext must be provided with an onClose function.');
  }

  if (!context.studioGroup) {
    throw new Error('ExerciseManagerContext must be provided with a studioGroup.');
  }

  if (!context.language) {
    throw new Error('ExerciseManagerContext must be provided with a language.');
  }
}

export function useExerciseManagerContext() {
  const context = useContext(ExerciseManagerContext);

  isValidExerciseManagerContext(context);

  return context;
}
